/**
 * Hook for managing saved searches.
 *
 * Standalone hook (no context provider needed) — fetches on mount when
 * authenticated and provides CRUD operations with optimistic delete.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './useAuth';
import {
  getSavedSearches,
  createSavedSearch,
  deleteSavedSearch as deleteSavedSearchApi,
} from '../utils/savedSearchesApi';
import type { SavedSearch, SearchParams } from '../utils/savedSearchesApi';
import { logger } from '../utils/logger';

export interface UseSavedSearchesReturn {
  savedSearches: SavedSearch[];
  loading: boolean;
  error: string | null;
  saveSearch: (name: string, searchParams: SearchParams) => Promise<void>;
  deleteSearch: (searchId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

export const useSavedSearches = (): UseSavedSearchesReturn => {
  const { isAuthenticated } = useAuth();
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSearches = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await getSavedSearches();
      setSavedSearches(response.items);
      setError(null);
    } catch (err) {
      logger.error('Failed to load saved searches', err);
      setError('Failed to load saved searches');
      setSavedSearches([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchSearches();
  }, [fetchSearches]);

  const saveSearch = useCallback(
    async (name: string, searchParams: SearchParams) => {
      await createSavedSearch(name, searchParams);
      await fetchSearches();
    },
    [fetchSearches]
  );

  const savedSearchesRef = useRef(savedSearches);
  savedSearchesRef.current = savedSearches;

  const deleteSearch = useCallback(
    async (searchId: string) => {
      const previousSearches = savedSearchesRef.current;

      // Optimistic removal
      setSavedSearches((prev) => prev.filter((s) => s.searchId !== searchId));

      try {
        await deleteSavedSearchApi(searchId);
      } catch (err) {
        logger.error('Failed to delete saved search', err);
        setSavedSearches(previousSearches);
        setError('Failed to delete saved search');
      }
    },
    []
  );

  const refresh = useCallback(async () => {
    await fetchSearches();
  }, [fetchSearches]);

  return {
    savedSearches,
    loading,
    error,
    saveSearch,
    deleteSearch,
    refresh,
  };
};
